import { me } from '@connected/api-client/data/auth/me';
import { isAuthenticated } from '@connected/api-client/lib/authentication';
import { checkRouteGuard } from '@connected/core';
import { createFileRoute, redirect } from '@tanstack/react-router';

import type { ElektraConditionContext } from '@/config/conditions';

import { elektraConditions, elektraRouteGuard, getRedirectUrlFromResult } from '@/config/routing';

/**
 * Catch-all route that handles undefined routes in elektra-app
 *
 * This route intercepts any path not explicitly defined (like /sso, /login, or /projects/**)
 * and redirects to portaal via SSO with an appropriate fallback route.
 *
 * TanStack Router's catch-all ($) only matches routes that don't have explicit definitions,
 * so /sso and /login routes will still work normally.
 */
export const Route = createFileRoute('/$')({
    beforeLoad: async ({ context, location }) => {
        // Try to get current user - may be null if not authenticated
        let currentUser = null;
        const isUserAuthenticated = isAuthenticated();

        if (isUserAuthenticated) {
            try {
                currentUser = await context.queryClient.ensureQueryData(me.queryOptions());
            }
            catch {
                // Failed to fetch user - treat as not authenticated
            }
        }

        // Build the condition context for route guard evaluation
        const conditionContext: ElektraConditionContext = {
            currentUser,
            isAuthenticated: isUserAuthenticated && currentUser !== null,
            currentPath: location.pathname,
            // Route guards don't need project-specific context
            hasInspectionKind: () => false,
            enabledElectricalProjectPriceQuote: false,
            enabledElectricalProjectPlanning: false,
            enableExtendedRiso: false,
            enableImpedanceMeasurementsPage: false,
            hasResidualCurrentCircuitBreakerDevices: false,
        };

        const result = checkRouteGuard({
            config: elektraRouteGuard,
            context: conditionContext,
            conditions: elektraConditions,
        });

        if (!result.allowed) {
            const redirectUrl = getRedirectUrlFromResult(result);
            // Use href for external redirects (to other domains via SSO)
            window.location.href = redirectUrl;
            // Return to prevent further execution while redirect happens
            return;
        }

        // If route is allowed (shouldn't normally happen for catch-all),
        // redirect to root which will handle it
        throw redirect({ to: '/' });
    },
    component: CatchAllPage,
});

function CatchAllPage() {
    return (
        <div className="flex h-full w-full items-center justify-center">
            <p>Redirecting...</p>
        </div>
    );
}
